import { useState } from "react";
import {
  DataTable,
  DataTableColumnHeader,
  Button,
  Tag,
  Checkbox,
  InputField,
  RadioGroup,
  RadioGroupItem,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@jem-hr/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import { Plus, Upload, Download, Trash2, AlignLeft, ListChecks } from "lucide-react";
import { BranchFilter } from "../components/BranchFilter";

interface Survey {
  id: string;
  name: string;
  createdBy: string;
  responses: string;
  launchDate: string;
  closeDate: string;
  status: "EXPIRED";
}

interface Question {
  id: string;
  type: "open-text" | "multiple-choice";
  text: string;
  options: string[];
}

const data: Survey[] = [
  {
    id: "1",
    name: "Employee Wellness",
    createdBy: "Francis Z",
    responses: "0/1",
    launchDate: "04/02/26",
    closeDate: "07/02/26",
    status: "EXPIRED",
  },
  {
    id: "2",
    name: "Emotions",
    createdBy: "Thando D",
    responses: "0/0",
    launchDate: "27/11/25",
    closeDate: "28/11/25",
    status: "EXPIRED",
  },
  {
    id: "3",
    name: "Tax on Bonus 2026",
    createdBy: "Beauty M",
    responses: "1/1",
    launchDate: "12/11/25",
    closeDate: "12/11/25",
    status: "EXPIRED",
  },
  {
    id: "4",
    name: "Employee check-in",
    createdBy: "Alexandra B",
    responses: "1/1",
    launchDate: "30/10/25",
    closeDate: "31/10/25",
    status: "EXPIRED",
  },
  {
    id: "5",
    name: "\uD83E\uDD6F KFC Festive Season Survey \uD83C\uDF84...",
    createdBy: "Kirsty R",
    responses: "0/1",
    launchDate: "07/10/25",
    closeDate: "18/10/25",
    status: "EXPIRED",
  },
  {
    id: "6",
    name: "\uD83C\uDF57 KFC Staff Happiness Survey ...",
    createdBy: "Kirsty R",
    responses: "0/1",
    launchDate: "30/09/25",
    closeDate: "07/10/25",
    status: "EXPIRED",
  },
  {
    id: "7",
    name: "Test Survey",
    createdBy: "Thando D",
    responses: "1/1",
    launchDate: "23/09/25",
    closeDate: "30/09/25",
    status: "EXPIRED",
  },
  {
    id: "8",
    name: "\uD83C\uDF57 KFC Staff Happiness Survey ...",
    createdBy: "Kirsty R",
    responses: "0/3",
    launchDate: "19/09/25",
    closeDate: "26/09/25",
    status: "EXPIRED",
  },
  {
    id: "9",
    name: "Employee Happiness Survey",
    createdBy: "Kirsty R",
    responses: "0/0",
    launchDate: "16/09/25",
    closeDate: "23/09/25",
    status: "EXPIRED",
  },
  {
    id: "10",
    name: "McDonalds Employee Happiness",
    createdBy: "Kirsty R",
    responses: "0/1",
    launchDate: "11/09/25",
    closeDate: "18/09/25",
    status: "EXPIRED",
  },
  {
    id: "11",
    name: "\uD83C\uDF54 Stop & Fix",
    createdBy: "Bakuena N",
    responses: "0/4",
    launchDate: "28/08/25",
    closeDate: "28/08/25",
    status: "EXPIRED",
  },
];

const columns: ColumnDef<Survey>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Survey Name" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-greyscale-text-title">
        {row.original.name}
      </span>
    ),
  },
  {
    accessorKey: "createdBy",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created By" />
    ),
  },
  {
    accessorKey: "responses",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Responses" />
    ),
  },
  {
    accessorKey: "launchDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Launch Date" />
    ),
  },
  {
    accessorKey: "closeDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Close Date" />
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <Tag variant="failed">{row.original.status}</Tag>
    ),
  },
  {
    id: "actions",
    cell: () => (
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-xxs">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-greyscale-text-caption hover:text-greyscale-text-title"
              >
                <Upload size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Export</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-greyscale-text-caption hover:text-greyscale-text-title"
              >
                <Download size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant="dark">Download</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    ),
  },
];

let nextQuestionId = 2;

function makeDefaultQuestion(type: "open-text" | "multiple-choice"): Question {
  return {
    id: String(nextQuestionId++),
    type,
    text: "",
    options: type === "multiple-choice" ? ["", ""] : [],
  };
}

export function Surveys() {
  const [showForm, setShowForm] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([
    { id: "1", type: "open-text", text: "", options: [] },
  ]);
  const [dateRange, setDateRange] = useState<
    "24-hours" | "3-days" | "7-days" | "custom"
  >("24-hours");

  const resetForm = () => {
    setShowForm(false);
    setQuestions([{ id: "1", type: "open-text", text: "", options: [] }]);
    setDateRange("24-hours");
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...updates } : q))
    );
  };

  const removeQuestion = (id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id));
  };

  const addQuestion = (type: "open-text" | "multiple-choice") => {
    setQuestions((prev) => [...prev, makeDefaultQuestion(type)]);
  };

  const updateOption = (questionId: string, optionIndex: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q;
        const options = [...q.options];
        options[optionIndex] = value;
        return { ...q, options };
      })
    );
  };

  const removeOption = (questionId: string, optionIndex: number) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q;
        return { ...q, options: q.options.filter((_, i) => i !== optionIndex) };
      })
    );
  };

  const addOption = (questionId: string) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q;
        return { ...q, options: [...q.options, ""] };
      })
    );
  };

  const changeQuestionType = (
    questionId: string,
    newType: "open-text" | "multiple-choice"
  ) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q;
        if (newType === q.type) return q;
        return {
          ...q,
          type: newType,
          options: newType === "multiple-choice" ? ["", ""] : [],
        };
      })
    );
  };

  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between -mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Surveys
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            Create and manage employee surveys to gather valuable feedback.
          </p>
        </div>
        {!showForm && (
          <Button
            leftIcon={<Plus size={16} />}
            onClick={() => setShowForm(true)}
          >
            Create Survey
          </Button>
        )}
      </div>

      {showForm ? (
        <div>
          <div className="max-w-[850px]">
            {/* Form heading */}
            <p className="text-base font-semibold text-greyscale-text-title mb-md">
              Create a new survey
            </p>

            <div className="flex flex-col gap-md">
              {/* Section 1 — Survey Details */}

              {/* Survey name */}
              <InputField
                label="Survey name"
                description="Give your survey a clear, descriptive name"
                placeholder="e.g. Employee Wellness Check-in"
              />

              {/* Select tags */}
              <div className="flex flex-col gap-xxxs">
                <div>
                  <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                    Select tags
                  </p>
                  <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                    Choose which groups will receive this survey
                  </p>
                </div>
                <BranchFilter
                  branches={["All Branches", "Head Office", "Warehouse", "Retail"]}
                  selectedBranches={[]}
                  label=""
                  placeholder="Select tags..."
                  searchPlaceholder="Search tags..."
                  className="max-w-full"
                />
              </div>

              {/* Date range */}
              <div className="flex flex-col gap-xxxs">
                <div>
                  <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                    Date range
                  </p>
                  <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                    How long should the survey remain open?
                  </p>
                </div>
                <RadioGroup
                  value={dateRange}
                  onValueChange={(v) =>
                    setDateRange(
                      v as "24-hours" | "3-days" | "7-days" | "custom"
                    )
                  }
                  className="flex gap-xxxs"
                >
                  <div className="flex items-center gap-xxs">
                    <RadioGroupItem value="24-hours" id="dr-24h" />
                    <Label htmlFor="dr-24h" className="text-sm">
                      24 hours
                    </Label>
                  </div>
                  <div className="flex items-center gap-xxs">
                    <RadioGroupItem value="3-days" id="dr-3d" />
                    <Label htmlFor="dr-3d" className="text-sm">
                      3 days
                    </Label>
                  </div>
                  <div className="flex items-center gap-xxs">
                    <RadioGroupItem value="7-days" id="dr-7d" />
                    <Label htmlFor="dr-7d" className="text-sm">
                      7 days
                    </Label>
                  </div>
                  <div className="flex items-center gap-xxs">
                    <RadioGroupItem value="custom" id="dr-custom" />
                    <Label htmlFor="dr-custom" className="text-sm">
                      Custom
                    </Label>
                  </div>
                </RadioGroup>

                {dateRange === "custom" && (
                  <div className="flex gap-sm mt-xxs">
                    <InputField
                      label="Start date"
                      type="date"
                    />
                    <InputField
                      label="End date"
                      type="date"
                    />
                  </div>
                )}
              </div>

              {/* Section 2 — Questions */}
              <div>
                <p className="text-sm font-semibold leading-18 text-greyscale-text-title mb-xxs">
                  Questions
                </p>

                <div className="flex flex-col gap-sm">
                  {questions.map((question, index) => (
                    <div
                      key={question.id}
                      className="rounded-lg border border-greyscale-border p-md"
                    >
                      {/* Question header */}
                      <div className="flex items-center justify-between mb-sm">
                        <div className="flex items-center gap-xxs">
                          <Tag variant="pink">Q{index + 1}</Tag>
                          <Select
                            value={question.type}
                            onValueChange={(v) =>
                              changeQuestionType(
                                question.id,
                                v as "open-text" | "multiple-choice"
                              )
                            }
                          >
                            <SelectTrigger className="w-[180px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open-text">
                                Open Text
                              </SelectItem>
                              <SelectItem value="multiple-choice">
                                Multiple Choice
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <button
                          type="button"
                          disabled={questions.length <= 1}
                          onClick={() => removeQuestion(question.id)}
                          className="text-greyscale-text-caption hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Question text */}
                      <InputField
                        label="Question"
                        placeholder="Enter your question"
                        value={question.text}
                        onChange={(e) =>
                          updateQuestion(question.id, {
                            text: e.target.value,
                          })
                        }
                      />

                      {/* Multiple choice options */}
                      {question.type === "multiple-choice" && (
                        <div className="mt-sm flex flex-col gap-xxs">
                          {question.options.map((option, optIdx) => (
                            <div
                              key={optIdx}
                              className="flex items-end gap-xxs"
                            >
                              <div className="flex-1">
                                <InputField
                                  label={
                                    optIdx === 0 ? "Options" : undefined
                                  }
                                  placeholder={`Option ${optIdx + 1}`}
                                  value={option}
                                  onChange={(e) =>
                                    updateOption(
                                      question.id,
                                      optIdx,
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <button
                                type="button"
                                disabled={question.options.length <= 2}
                                onClick={() =>
                                  removeOption(question.id, optIdx)
                                }
                                className="mb-[10px] text-greyscale-text-caption hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                          <Button
                            variant="ghost"
                            className="w-fit"
                            onClick={() => addOption(question.id)}
                          >
                            + Add option
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add question buttons */}
                <div className="flex gap-xxs mt-sm">
                  <Button
                    variant="outline"
                    leftIcon={<AlignLeft size={16} />}
                    onClick={() => addQuestion("open-text")}
                  >
                    Open Text
                  </Button>
                  <Button
                    variant="outline"
                    leftIcon={<ListChecks size={16} />}
                    onClick={() => addQuestion("multiple-choice")}
                  >
                    Multiple Choice
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-greyscale-border mt-lg" />
          <div className="flex items-center justify-between py-lg">
            <Button variant="outline" onClick={resetForm}>
              Go Back
            </Button>
            <div className="flex items-center gap-xxs">
              <Button variant="outline">Save Draft</Button>
              <Button>Publish Survey</Button>
            </div>
          </div>
        </div>
      ) : (
        /* Table */
        <DataTable
          columns={columns}
          data={data}
          showPagination
          showToolbar={false}
        />
      )}
    </div>
  );
}
